import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  EcosystemOfficeUsageDTO,
  EcosystemTenantUsageDTO,
} from '@/domain/repositories/IReportRepository';
import { featureKeyLabel } from '@/domain/types/planCatalog';
import { ActionMenu, Button, Input, type ActionMenuItem } from '@/presentation/components/common';
import { useEcosystemUsage } from '@/presentation/hooks/useEcosystemUsage';
import { WhatsAppBonusModal } from '@/presentation/components/ecosystem/WhatsAppBonusModal';
import { ClinicInvoiceModal } from '@/presentation/components/billing/ClinicInvoiceModal';

type Tab = 'monthly' | 'history';
type SortField = 'whatsapp' | 'email' | 'app' | 'nombre';
type SortDirection = 'asc' | 'desc';
/** Filtro de estado de clínica (valores de tenant.estado en core). */
type StatusFilter = 'all' | 'active' | 'active_or_trial' | 'trial' | 'suspended';

/** Near-limit when fewer than this many messages remain. */
const WHATSAPP_NEAR_LIMIT_REMAINING = 50;

function normalizeEstado(estado: string): string {
  return estado.trim().toLowerCase();
}

function matchesStatusFilter(estado: string, filter: StatusFilter): boolean {
  if (filter === 'all') return true;
  const e = normalizeEstado(estado);
  switch (filter) {
    case 'active':
      return e === 'active';
    case 'trial':
      return e === 'trial';
    case 'suspended':
      return e === 'suspended';
    case 'active_or_trial':
      return e === 'active' || e === 'trial';
    default:
      return true;
  }
}

function parseOptionalNonNegInt(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

function isWhatsappNearLimit(used: number, limit: number): boolean {
  if (limit <= 0) return false;
  return used > limit - WHATSAPP_NEAR_LIMIT_REMAINING;
}

function formatWhatsappUsage(used: number, limit: number): string {
  if (limit <= 0) return used.toLocaleString();
  return `${used.toLocaleString()}/${limit.toLocaleString()}`;
}

function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function previousPeriod(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

type ClinicOfficeRow = {
  officeId: number | null;
  officeName: string;
  whatsappMessages: number;
  emailMessages: number;
};

type ClinicRow = {
  tenantId: string;
  nombre: string;
  estado: string;
  whatsappMessages: number;
  whatsappMonthlyLimit: number;
  whatsappBonus: number;
  conexionMonthlyPrice: number;
  whatsappOverageMonthlyPrice: number;
  emailMessages: number;
  appPatientDevices: number;
  featuresEnabled: string[];
  offices: ClinicOfficeRow[];
};

function ChevronIcon({ expanded, className }: { expanded: boolean; className?: string }) {
  return (
    <svg
      className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''} ${className ?? ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}

function ClinicRowActions({
  item,
  actionsLabel,
  viewFeaturesLabel,
  bonusLabel,
  invoiceLabel,
  onViewFeatures,
  onBonusWhatsapp,
  onExtractInvoice,
}: {
  item: ClinicRow;
  actionsLabel: string;
  viewFeaturesLabel: string;
  bonusLabel: string;
  invoiceLabel: string;
  onViewFeatures: (row: ClinicRow) => void;
  onBonusWhatsapp?: (row: ClinicRow) => void;
  onExtractInvoice: (row: ClinicRow) => void;
}) {
  const items: ActionMenuItem[] = [
    {
      icon: '👁',
      label: viewFeaturesLabel,
      onClick: () => onViewFeatures(item),
      disabled: item.featuresEnabled.length === 0,
    },
  ];
  if (onBonusWhatsapp) {
    items.push({
      icon: '🎁',
      label: bonusLabel,
      onClick: () => onBonusWhatsapp(item),
    });
  }
  items.push({
    icon: '📄',
    label: invoiceLabel,
    onClick: () => onExtractInvoice(item),
    separator: true,
  });
  return <ActionMenu triggerTitle={actionsLabel} items={items} />;
}

function WhatsappUsageValue({
  value,
  limit,
  nearLimitTitle,
  className,
}: {
  value: number;
  limit: number;
  nearLimitTitle: string;
  className?: string;
}) {
  const near = isWhatsappNearLimit(value, limit);
  return (
    <span
      className={`inline-flex items-center justify-end gap-1 tabular-nums ${
        near ? 'font-semibold text-amber-600 dark:text-amber-400' : ''
      } ${className ?? ''}`}
      title={near ? nearLimitTitle : undefined}
    >
      {near ? <WarningIcon className="h-3.5 w-3.5 flex-shrink-0" /> : null}
      {formatWhatsappUsage(value, limit)}
    </span>
  );
}

function FeaturesModal({
  title,
  subtitle,
  items,
  emptyLabel,
  closeLabel,
  onClose,
}: {
  title: string;
  subtitle?: string;
  items: { label: string; value?: string; title?: string }[];
  emptyLabel: string;
  closeLabel: string;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="px-4 pt-5 pb-4 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{title}</h3>
            {subtitle ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>
            ) : null}
            <div className="mt-4 max-h-[60vh] overflow-auto">
              {items.length === 0 ? (
                <p className="text-sm text-gray-500">{emptyLabel}</p>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-gray-700">
                  {items.map((item) => (
                    <li
                      key={`${item.label}-${item.value ?? ''}`}
                      className="flex items-baseline justify-between gap-3 py-2"
                    >
                      <span
                        className="text-sm text-gray-700 dark:text-gray-200"
                        title={item.title}
                      >
                        {item.label}
                      </span>
                      {item.value != null ? (
                        <span className="text-sm font-semibold tabular-nums text-slate-900 dark:text-white shrink-0">
                          {item.value}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="bg-slate-50 dark:bg-gray-900/50 px-4 py-3 sm:px-6 flex justify-end">
            <Button variant="secondary" size="sm" onClick={onClose}>
              {closeLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ClinicsUsageTable({
  rows,
  emptyLabel,
  clinicLabel,
  statusLabel,
  featuresLabel,
  viewFeaturesLabel,
  actionsLabel,
  bonusLabel,
  invoiceLabel,
  whatsappLabel,
  emailLabel,
  appLabel,
  whatsappNearLimitTitle,
  branchLabel,
  branchesLabel,
  unassignedBranchLabel,
  expandClinicLabel,
  collapseClinicLabel,
  sortField,
  sortDirection,
  onSort,
  onViewFeatures,
  onBonusWhatsapp,
  onExtractInvoice,
}: {
  rows: ClinicRow[];
  emptyLabel: string;
  clinicLabel: string;
  statusLabel: string;
  featuresLabel: string;
  viewFeaturesLabel: string;
  actionsLabel: string;
  bonusLabel: string;
  invoiceLabel: string;
  whatsappLabel: string;
  emailLabel: string;
  appLabel: string;
  whatsappNearLimitTitle: string;
  branchLabel: string;
  branchesLabel: (count: number) => string;
  unassignedBranchLabel: string;
  expandClinicLabel: string;
  collapseClinicLabel: string;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  onViewFeatures: (row: ClinicRow) => void;
  onBonusWhatsapp?: (row: ClinicRow) => void;
  onExtractInvoice: (row: ClinicRow) => void;
}) {
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  const toggleExpanded = (tenantId: string) => {
    setExpandedIds((prev) =>
      prev.includes(tenantId) ? prev.filter((id) => id !== tenantId) : [...prev, tenantId]
    );
  };

  const resolveOfficeName = (name: string) =>
    name === 'Sin sucursal' ? unassignedBranchLabel : name;

  const sortIcon = (field: SortField) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const sortThClass =
    'cursor-pointer select-none bg-slate-50 px-3 py-3 text-xs font-medium uppercase text-gray-500 hover:bg-slate-100 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800';

  const colSpan = 7;

  return (
    <>
      {/* Cards mobile */}
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2 md:hidden">
        {rows.length === 0 ? (
          <div className="px-3 py-8 text-center text-sm text-slate-500">{emptyLabel}</div>
        ) : (
          rows.map((item) => {
            const initial = (item.nombre.trim().charAt(0) || '?').toUpperCase();
            const waNear = isWhatsappNearLimit(item.whatsappMessages, item.whatsappMonthlyLimit);
            const canExpand = item.offices.length > 0;
            const expanded = canExpand && expandedIds.includes(item.tenantId);
            return (
              <div key={item.tenantId} className="panel-list-card px-3.5 py-3">
                <div className="flex items-start gap-3">
                  {canExpand ? (
                    <button
                      type="button"
                      onClick={() => toggleExpanded(item.tenantId)}
                      className="mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600 transition-colors hover:bg-slate-200 dark:bg-gray-700 dark:text-slate-200 dark:hover:bg-gray-600"
                      aria-expanded={expanded}
                      aria-label={expanded ? collapseClinicLabel : expandClinicLabel}
                      title={expanded ? collapseClinicLabel : expandClinicLabel}
                    >
                      <ChevronIcon expanded={expanded} />
                    </button>
                  ) : (
                    <span className="mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 text-sm font-bold text-slate-700 dark:bg-gray-700 dark:text-slate-200">
                      {initial}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p
                          className="truncate text-[15px] font-semibold leading-snug text-slate-900 dark:text-white"
                          title={item.nombre}
                        >
                          {item.nombre}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                          {item.estado}
                          {canExpand ? ` · ${branchesLabel(item.offices.length)}` : ''}
                        </p>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <p
                          className={`inline-flex items-center justify-end gap-1 text-[15px] font-bold tabular-nums leading-snug ${
                            waNear
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-teal-700 dark:text-teal-300'
                          }`}
                          title={waNear ? whatsappNearLimitTitle : undefined}
                        >
                          {waNear ? <WarningIcon className="h-3.5 w-3.5 flex-shrink-0" /> : null}
                          {formatWhatsappUsage(item.whatsappMessages, item.whatsappMonthlyLimit)}
                        </p>
                        <p className="mt-0.5 text-[11px] text-slate-500">WA</p>
                      </div>
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <span className="rounded-md bg-slate-50 px-1.5 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-gray-900/50 dark:text-slate-300">
                        Email {item.emailMessages.toLocaleString()}
                      </span>
                      <span className="rounded-md bg-slate-50 px-1.5 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-gray-900/50 dark:text-slate-300">
                        App {item.appPatientDevices.toLocaleString()}
                      </span>
                      <span className="rounded-md bg-slate-50 px-1.5 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-gray-900/50 dark:text-slate-300">
                        {featuresLabel} {item.featuresEnabled.length}
                      </span>
                      {item.whatsappBonus > 0 ? (
                        <span className="rounded-md bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                          +{item.whatsappBonus} WA
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-2.5 flex justify-end border-t border-slate-100 pt-2 dark:border-gray-700/80">
                      <ClinicRowActions
                        item={item}
                        actionsLabel={actionsLabel}
                        viewFeaturesLabel={viewFeaturesLabel}
                        bonusLabel={bonusLabel}
                        invoiceLabel={invoiceLabel}
                        onViewFeatures={onViewFeatures}
                        onBonusWhatsapp={onBonusWhatsapp}
                        onExtractInvoice={onExtractInvoice}
                      />
                    </div>

                    {expanded ? (
                      <ul className="mt-2 space-y-1.5 border-t border-slate-100 pt-2 dark:border-gray-700/80">
                        {item.offices.map((office) => (
                          <li
                            key={`${item.tenantId}-${office.officeId ?? 'none'}`}
                            className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5 dark:bg-gray-900/40"
                          >
                            <span className="min-w-0 truncate text-xs font-medium text-slate-700 dark:text-slate-200">
                              {resolveOfficeName(office.officeName)}
                            </span>
                            <span className="shrink-0 text-[11px] tabular-nums text-slate-500 dark:text-slate-400">
                              WA {office.whatsappMessages.toLocaleString()} · Email{' '}
                              {office.emailMessages.toLocaleString()}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Tabla md+ (iPad / desktop) */}
      <div className="hidden min-h-0 flex-1 overflow-x-auto md:block">
        <table className="min-w-[640px] w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
          <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-gray-900">
            <tr>
              <th
                className={`${sortThClass} text-left`}
                onClick={() => onSort('nombre')}
                aria-sort={
                  sortField === 'nombre'
                    ? sortDirection === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                }
              >
                {clinicLabel}
                {sortIcon('nombre')}
              </th>
              <th className="bg-slate-50 px-3 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:bg-gray-900 dark:text-gray-300">
                {statusLabel}
              </th>
              <th
                className={`${sortThClass} text-right`}
                onClick={() => onSort('whatsapp')}
                aria-sort={
                  sortField === 'whatsapp'
                    ? sortDirection === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                }
              >
                {whatsappLabel}
                {sortIcon('whatsapp')}
              </th>
              <th
                className={`${sortThClass} text-right`}
                onClick={() => onSort('email')}
                aria-sort={
                  sortField === 'email'
                    ? sortDirection === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                }
              >
                {emailLabel}
                {sortIcon('email')}
              </th>
              <th
                className={`${sortThClass} py-3.5 text-right`}
                onClick={() => onSort('app')}
                aria-sort={
                  sortField === 'app'
                    ? sortDirection === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                }
              >
                {appLabel}
                {sortIcon('app')}
              </th>
              <th className="bg-slate-50 px-3 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:bg-gray-900 dark:text-gray-300">
                {featuresLabel}
              </th>
              <th className="bg-slate-50 px-3 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:bg-gray-900 dark:text-gray-300">
                {actionsLabel}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="px-3 py-6 text-center text-gray-500">
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              rows.map((item) => {
                const canExpand = item.offices.length > 0;
                const expanded = canExpand && expandedIds.includes(item.tenantId);
                return (
                  <React.Fragment key={item.tenantId}>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      <td className="px-3 py-3 font-medium text-gray-900 dark:text-white">
                        <div className="flex items-start gap-2">
                          {canExpand ? (
                            <button
                              type="button"
                              onClick={() => toggleExpanded(item.tenantId)}
                              className="mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-gray-700 dark:hover:text-white"
                              aria-expanded={expanded}
                              aria-label={expanded ? collapseClinicLabel : expandClinicLabel}
                              title={expanded ? collapseClinicLabel : expandClinicLabel}
                            >
                              <ChevronIcon expanded={expanded} />
                            </button>
                          ) : (
                            <span className="inline-block h-6 w-6 flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <div title={item.nombre}>{item.nombre}</div>
                            {canExpand ? (
                              <div className="mt-0.5 text-[11px] font-normal text-slate-500 dark:text-slate-400">
                                {branchesLabel(item.offices.length)}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-gray-600 dark:text-gray-300">{item.estado}</td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex flex-col items-end gap-0.5">
                          <WhatsappUsageValue
                            value={item.whatsappMessages}
                            limit={item.whatsappMonthlyLimit}
                            nearLimitTitle={whatsappNearLimitTitle}
                          />
                          {item.whatsappBonus > 0 ? (
                            <span className="text-[10px] font-medium text-amber-700 dark:text-amber-400">
                              +{item.whatsappBonus.toLocaleString()} bonus
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">{item.emailMessages.toLocaleString()}</td>
                      <td className="px-3 py-3.5 text-right tabular-nums">
                        {item.appPatientDevices.toLocaleString()}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-gray-600 dark:text-gray-300">
                        {item.featuresEnabled.length > 0 ? item.featuresEnabled.length : '—'}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <ClinicRowActions
                          item={item}
                          actionsLabel={actionsLabel}
                          viewFeaturesLabel={viewFeaturesLabel}
                          bonusLabel={bonusLabel}
                          invoiceLabel={invoiceLabel}
                          onViewFeatures={onViewFeatures}
                          onBonusWhatsapp={onBonusWhatsapp}
                          onExtractInvoice={onExtractInvoice}
                        />
                      </td>
                    </tr>
                    {expanded
                      ? item.offices.map((office) => (
                          <tr
                            key={`${item.tenantId}-${office.officeId ?? 'none'}`}
                            className="bg-slate-50/80 dark:bg-gray-900/40"
                          >
                            <td className="px-3 py-2 pl-12 text-slate-600 dark:text-slate-300">
                              {office.officeName !== 'Sin sucursal' ? (
                                <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                                  {branchLabel}
                                </span>
                              ) : null}
                              <div className="font-medium text-slate-700 dark:text-slate-200">
                                {resolveOfficeName(office.officeName)}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-slate-400">—</td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">
                              {office.whatsappMessages.toLocaleString()}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">
                              {office.emailMessages.toLocaleString()}
                            </td>
                            <td className="px-3 py-2 text-right text-slate-400">—</td>
                            <td className="px-3 py-2" />
                            <td className="px-3 py-2" />
                          </tr>
                        ))
                      : null}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

type ClinicFilters = {
  searchTerm: string;
  statusFilter: StatusFilter;
  whatsappMin: number | null;
  whatsappMax: number | null;
};

function filterAndSortClinics(
  tenants: EcosystemTenantUsageDTO[],
  filters: ClinicFilters,
  usage: (t: EcosystemTenantUsageDTO) => { whatsapp: number; email: number },
  officeUsage: (office: EcosystemOfficeUsageDTO) => { whatsapp: number; email: number },
  sortField: SortField,
  sortDirection: SortDirection
): ClinicRow[] {
  const term = filters.searchTerm.trim().toLowerCase();
  const rows: ClinicRow[] = tenants.map((item) => {
    const u = usage(item);
    return {
      tenantId: item.tenantId,
      nombre: item.nombre,
      estado: item.estado,
      whatsappMessages: u.whatsapp,
      whatsappMonthlyLimit: item.whatsappMonthlyLimit ?? 0,
      whatsappBonus: item.whatsappBonus ?? 0,
      conexionMonthlyPrice: item.conexionMonthlyPrice ?? 0,
      whatsappOverageMonthlyPrice: item.whatsappOverageMonthlyPrice ?? 0,
      emailMessages: u.email,
      appPatientDevices: item.appPatientDevices,
      featuresEnabled: item.featuresEnabled,
      offices: (item.offices ?? []).map((office) => {
        const ou = officeUsage(office);
        return {
          officeId: office.officeId,
          officeName: office.officeName,
          whatsappMessages: ou.whatsapp,
          emailMessages: ou.email,
        };
      }),
    };
  });

  const filtered = rows.filter((item) => {
    if (!matchesStatusFilter(item.estado, filters.statusFilter)) return false;

    if (filters.whatsappMin != null && item.whatsappMessages < filters.whatsappMin) {
      return false;
    }
    if (filters.whatsappMax != null && item.whatsappMessages > filters.whatsappMax) {
      return false;
    }

    if (!term) return true;
    return (
      item.nombre.toLowerCase().includes(term) ||
      item.estado.toLowerCase().includes(term) ||
      item.featuresEnabled.some(
        (f) =>
          f.toLowerCase().includes(term) ||
          featureKeyLabel(f).toLowerCase().includes(term)
      )
    );
  });

  const dir = sortDirection === 'asc' ? 1 : -1;
  filtered.sort((a, b) => {
    let cmp = 0;
    switch (sortField) {
      case 'nombre':
        cmp = a.nombre.localeCompare(b.nombre, 'es');
        break;
      case 'whatsapp':
        cmp = a.whatsappMessages - b.whatsappMessages;
        break;
      case 'email':
        cmp = a.emailMessages - b.emailMessages;
        break;
      case 'app':
        cmp = a.appPatientDevices - b.appPatientDevices;
        break;
    }
    if (cmp !== 0) return cmp * dir;
    return a.nombre.localeCompare(b.nombre, 'es');
  });

  return filtered;
}

export const EcosystemUsagePage: React.FC = () => {
  const { t } = useTranslation();
  const period = currentPeriod();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [whatsappMinRaw, setWhatsappMinRaw] = useState('');
  const [whatsappMaxRaw, setWhatsappMaxRaw] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('monthly');
  const [historyPeriod, setHistoryPeriod] = useState(previousPeriod());
  const [showAdoptionModal, setShowAdoptionModal] = useState(false);
  const [clinicFeatures, setClinicFeatures] = useState<ClinicRow | null>(null);
  const [bonusTarget, setBonusTarget] = useState<ClinicRow | null>(null);
  const [invoiceTarget, setInvoiceTarget] = useState<ClinicRow | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortField, setSortField] = useState<SortField>('whatsapp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [kpiOpen, setKpiOpen] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : true
  );
  const { report, isLoading, error, refetch } = useEcosystemUsage(period);

  const whatsappMin = useMemo(() => parseOptionalNonNegInt(whatsappMinRaw), [whatsappMinRaw]);
  const whatsappMax = useMemo(() => parseOptionalNonNegInt(whatsappMaxRaw), [whatsappMaxRaw]);

  const clinicFilters = useMemo<ClinicFilters>(
    () => ({
      searchTerm,
      statusFilter,
      whatsappMin,
      whatsappMax,
    }),
    [searchTerm, statusFilter, whatsappMin, whatsappMax]
  );

  const statusFilterLabel = (value: StatusFilter): string => {
    switch (value) {
      case 'active':
        return t('pages.ecosystemUsage.statusActive');
      case 'active_or_trial':
        return t('pages.ecosystemUsage.statusActiveOrTrial');
      case 'trial':
        return t('pages.ecosystemUsage.statusTrial');
      case 'suspended':
        return t('pages.ecosystemUsage.statusSuspended');
      default:
        return t('pages.ecosystemUsage.statusAll');
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'nombre' ? 'asc' : 'desc');
    }
  };

  const historyPeriods = report?.historyPeriods?.length
    ? report.historyPeriods
    : report?.monthlySeries.map((p) => p.period) ?? [];

  const defaultHistoryPeriod = useMemo(() => {
    const previousPeriods = historyPeriods.filter((p) => p !== period);
    return previousPeriods[previousPeriods.length - 1]
      ?? historyPeriods[historyPeriods.length - 1]
      ?? previousPeriod();
  }, [historyPeriods, period]);

  useEffect(() => {
    if (historyPeriods.length === 0) return;
    if (!historyPeriods.includes(historyPeriod)) {
      setHistoryPeriod(defaultHistoryPeriod);
    }
  }, [defaultHistoryPeriod, historyPeriods, historyPeriod]);

  const monthlyRows = useMemo(() => {
    if (!report) return [];
    return filterAndSortClinics(
      report.tenants,
      clinicFilters,
      (item) => ({
        whatsapp: item.whatsappMessages,
        email: item.emailMessages,
      }),
      (office) => ({
        whatsapp: office.whatsappMessages,
        email: office.emailMessages,
      }),
      sortField,
      sortDirection
    );
  }, [report, clinicFilters, sortField, sortDirection]);

  useEffect(() => {
    if (!bonusTarget) return;
    const fresh = monthlyRows.find((r) => r.tenantId === bonusTarget.tenantId);
    if (!fresh) return;
    if (
      fresh.whatsappBonus !== bonusTarget.whatsappBonus ||
      fresh.whatsappMonthlyLimit !== bonusTarget.whatsappMonthlyLimit ||
      fresh.whatsappMessages !== bonusTarget.whatsappMessages
    ) {
      setBonusTarget(fresh);
    }
  }, [monthlyRows, bonusTarget]);

  const historyRows = useMemo(() => {
    if (!report) return [];
    return filterAndSortClinics(
      report.tenants,
      clinicFilters,
      (item) => {
        const h = item.monthlyHistory.find((m) => m.period === historyPeriod);
        return {
          whatsapp: h?.whatsappMessages ?? 0,
          email: h?.emailMessages ?? 0,
        };
      },
      (office) => {
        const h = office.monthlyHistory.find((m) => m.period === historyPeriod);
        return {
          whatsapp: h?.whatsappMessages ?? 0,
          email: h?.emailMessages ?? 0,
        };
      },
      sortField,
      sortDirection
    );
  }, [report, clinicFilters, historyPeriod, sortField, sortDirection]);

  const historyMonthBounds = useMemo(() => {
    if (historyPeriods.length === 0) {
      return { min: previousPeriod(), max: period };
    }
    return {
      min: historyPeriods[0],
      max: historyPeriods[historyPeriods.length - 1],
    };
  }, [historyPeriods, period]);

  const tabs: { key: Tab; label: string }[] = [
    { key: 'monthly', label: t('pages.ecosystemUsage.tabMonthly') },
    { key: 'history', label: t('pages.ecosystemUsage.tabHistory') },
  ];

  const tableLabels = {
    emptyLabel: t('common.noResults'),
    clinicLabel: t('pages.ecosystemUsage.clinic'),
    statusLabel: t('pages.ecosystemUsage.status'),
    featuresLabel: t('pages.ecosystemUsage.features'),
    viewFeaturesLabel: t('pages.ecosystemUsage.viewFeatures'),
    actionsLabel: t('common.actions'),
    bonusLabel: t('pages.ecosystemUsage.bonusWhatsapp'),
    invoiceLabel: t('pages.ecosystemUsage.extractInvoice'),
    whatsappLabel: t('pages.ecosystemUsage.whatsapp'),
    emailLabel: t('pages.ecosystemUsage.email'),
    appLabel: t('pages.ecosystemUsage.appDevices'),
    whatsappNearLimitTitle: t('pages.ecosystemUsage.whatsappNearLimit', {
      remaining: WHATSAPP_NEAR_LIMIT_REMAINING,
    }),
    branchLabel: t('pages.ecosystemUsage.branch'),
    branchesLabel: (count: number) => t('pages.ecosystemUsage.branches', { count }),
    unassignedBranchLabel: t('pages.ecosystemUsage.unassignedBranch'),
    expandClinicLabel: t('pages.ecosystemUsage.expandClinic'),
    collapseClinicLabel: t('pages.ecosystemUsage.collapseClinic'),
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setWhatsappMinRaw('');
    setWhatsappMaxRaw('');
    if (activeTab === 'history') {
      setHistoryPeriod(defaultHistoryPeriod);
    }
  };

  const whatsappRangeChipLabel = (() => {
    if (whatsappMin != null && whatsappMax != null) {
      return `${t('pages.ecosystemUsage.whatsapp')}: ${whatsappMin.toLocaleString()}–${whatsappMax.toLocaleString()}`;
    }
    if (whatsappMin != null) {
      return `${t('pages.ecosystemUsage.whatsapp')}: ≥ ${whatsappMin.toLocaleString()}`;
    }
    if (whatsappMax != null) {
      return `${t('pages.ecosystemUsage.whatsapp')}: ≤ ${whatsappMax.toLocaleString()}`;
    }
    return null;
  })();

  const activeFilterChips = [
    ...(searchTerm.trim()
      ? [{
          key: 'search',
          label: `${t('pages.ecosystemUsage.filterName')}: ${searchTerm.trim()}`,
          onRemove: () => setSearchTerm(''),
        }]
      : []),
    ...(statusFilter !== 'all'
      ? [{
          key: 'status',
          label: `${t('pages.ecosystemUsage.status')}: ${statusFilterLabel(statusFilter)}`,
          onRemove: () => setStatusFilter('all'),
        }]
      : []),
    ...(whatsappRangeChipLabel
      ? [{
          key: 'whatsapp',
          label: whatsappRangeChipLabel,
          onRemove: () => {
            setWhatsappMinRaw('');
            setWhatsappMaxRaw('');
          },
        }]
      : []),
    ...(activeTab === 'history' && historyPeriod !== defaultHistoryPeriod
      ? [{
          key: 'month',
          label: `${t('pages.ecosystemUsage.monthFilter')}: ${historyPeriod}`,
          onRemove: () => setHistoryPeriod(defaultHistoryPeriod),
        }]
      : []),
  ];

  return (
    <div className="panel-page h-full min-w-0 flex flex-col !pb-3 overflow-hidden">
      <div className="panel-page-header flex-shrink-0 !items-end">
        <div className="min-w-0">
          <h1 className="panel-page-title">{t('pages.ecosystemUsage.title')}</h1>
          <p className="panel-page-subtitle hidden sm:block">
            {t('pages.ecosystemUsage.subtitle')}
          </p>
          {activeTab === 'monthly' ? (
            <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400 sm:hidden">
              {t('pages.ecosystemUsage.currentMonth')}: {period}
            </p>
          ) : null}
        </div>
        <div className="flex w-full flex-wrap items-end gap-2 sm:w-auto">
          {activeTab === 'monthly' ? (
            <div className="mr-1 hidden text-right sm:block">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {t('pages.ecosystemUsage.currentMonth')}
              </p>
              <p className="mt-0.5 text-sm font-semibold tabular-nums text-slate-900 dark:text-white">
                {period}
              </p>
            </div>
          ) : null}
          <Button variant="secondary" size="sm" onClick={() => setShowAdoptionModal(true)} className="flex-1 sm:flex-none">
            {t('pages.ecosystemUsage.tabFeatures')}
          </Button>
          <Button variant="secondary" size="sm" onClick={refetch} className="flex-1 sm:flex-none">
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex-shrink-0 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error.message}</p>
        </div>
      )}

      {isLoading && !report ? (
        <div className="flex items-center justify-center h-40 text-gray-500">{t('common.loading')}</div>
      ) : (
        <>
          {activeTab === 'monthly' ? (
            <div className="flex-shrink-0 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setKpiOpen((v) => !v)}
                  className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                    kpiOpen
                      ? 'border-teal-500/50 bg-teal-50 text-teal-800 dark:border-teal-400/40 dark:bg-teal-900/30 dark:text-teal-200'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700'
                  }`}
                  aria-expanded={kpiOpen}
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
                    />
                  </svg>
                  {kpiOpen ? t('pages.ecosystemUsage.hideKpis') : t('pages.ecosystemUsage.showKpis')}
                  <svg
                    className={`h-3.5 w-3.5 transition-transform ${kpiOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {!kpiOpen && (
                  <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-teal-500/40 bg-teal-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-teal-800 dark:border-teal-400/40 dark:bg-teal-900/20 dark:text-teal-200">
                      <span className="truncate">
                        {t('pages.ecosystemUsage.whatsapp')}:{' '}
                        {(report?.totals.whatsappMessages ?? 0).toLocaleString()}
                      </span>
                    </span>
                    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-blue-500/40 bg-blue-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-blue-800 dark:border-blue-400/40 dark:bg-blue-900/20 dark:text-blue-200">
                      <span className="truncate">
                        {t('pages.ecosystemUsage.email')}:{' '}
                        {(report?.totals.emailMessages ?? 0).toLocaleString()}
                      </span>
                    </span>
                    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-green-500/40 bg-green-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-green-800 dark:border-green-400/40 dark:bg-green-900/20 dark:text-green-200">
                      <span className="truncate">
                        {t('pages.ecosystemUsage.appDevices')}:{' '}
                        {(report?.totals.appPatientDevices ?? 0).toLocaleString()}
                      </span>
                    </span>
                  </div>
                )}
              </div>

              {kpiOpen && (
                <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-3">
                  <div className="panel-stat !p-2.5 sm:!p-3.5 border-l-4 border-teal-500 md:!p-4">
                    <p className="panel-stat-label">{t('pages.ecosystemUsage.whatsapp')}</p>
                    <p className="panel-stat-value !mt-0.5 text-base sm:text-lg md:text-2xl">
                      {(report?.totals.whatsappMessages ?? 0).toLocaleString()}
                    </p>
                    <p className="mt-0.5 text-[11px] leading-tight text-gray-500">
                      {report?.totals.tenantsWithWhatsappUsage ?? 0}{' '}
                      {t('pages.ecosystemUsage.tenantsUsing')}
                    </p>
                  </div>
                  <div className="panel-stat !p-2.5 sm:!p-3.5 border-l-4 border-blue-500 md:!p-4">
                    <p className="panel-stat-label">{t('pages.ecosystemUsage.email')}</p>
                    <p className="panel-stat-value !mt-0.5 text-base sm:text-lg md:text-2xl">
                      {(report?.totals.emailMessages ?? 0).toLocaleString()}
                    </p>
                    <p className="mt-0.5 text-[11px] leading-tight text-gray-500">
                      {report?.totals.tenantsWithEmailUsage ?? 0}{' '}
                      {t('pages.ecosystemUsage.tenantsUsing')}
                    </p>
                  </div>
                  <div className="panel-stat !p-2.5 sm:!p-3.5 border-l-4 border-green-500 md:!p-4">
                    <p className="panel-stat-label">{t('pages.ecosystemUsage.appDevices')}</p>
                    <p className="panel-stat-value !mt-0.5 text-base sm:text-lg md:text-2xl">
                      {(report?.totals.appPatientDevices ?? 0).toLocaleString()}
                    </p>
                    <p className="mt-0.5 text-[11px] leading-tight text-gray-500">
                      {report?.totals.tenantsWithAppUsage ?? 0}{' '}
                      {t('pages.ecosystemUsage.tenantsUsing')}
                    </p>
                  </div>
                  <div className="panel-stat !p-2.5 sm:!p-3.5 border-l-4 border-orange-500 md:!p-4">
                    <p className="panel-stat-label">{t('pages.ecosystemUsage.tenants')}</p>
                    <p className="panel-stat-value !mt-0.5 text-base sm:text-lg md:text-2xl">
                      {report?.totals.tenants ?? 0}
                    </p>
                    <p className="mt-0.5 text-[11px] leading-tight text-gray-500">
                      {report?.period}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <div className="border-b border-slate-200 dark:border-gray-700 flex-shrink-0">
            <nav className="-mb-px flex gap-3 overflow-x-auto sm:gap-6">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`py-2.5 sm:py-3 px-1 border-b-2 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="flex-shrink-0 space-y-2 border-b border-slate-200 p-2 dark:border-gray-700">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setFiltersOpen((v) => !v)}
                  className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                    filtersOpen || activeFilterChips.length > 0
                      ? 'border-teal-500/50 bg-teal-50 text-teal-800 dark:border-teal-400/40 dark:bg-teal-900/30 dark:text-teal-200'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700'
                  }`}
                  aria-expanded={filtersOpen}
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M6 10h12M9 16h6" />
                  </svg>
                  {filtersOpen ? t('common.hideFilters') : t('common.showFilters')}
                  {activeFilterChips.length > 0 && (
                    <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-teal-600 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white">
                      {activeFilterChips.length}
                    </span>
                  )}
                  <svg
                    className={`h-3.5 w-3.5 transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <select
                  value={`${sortField}:${sortDirection}`}
                  onChange={(e) => {
                    const [field, dir] = e.target.value.split(':') as [SortField, SortDirection];
                    setSortField(field);
                    setSortDirection(dir);
                  }}
                  className="block h-9 max-w-full rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-200 md:hidden"
                  aria-label={t('pages.ecosystemUsage.sortBy')}
                  title={t('pages.ecosystemUsage.sortBy')}
                >
                  <option value="whatsapp:desc">{t('pages.ecosystemUsage.sortWhatsapp')} ↓</option>
                  <option value="whatsapp:asc">{t('pages.ecosystemUsage.sortWhatsapp')} ↑</option>
                  <option value="email:desc">{t('pages.ecosystemUsage.sortEmail')} ↓</option>
                  <option value="email:asc">{t('pages.ecosystemUsage.sortEmail')} ↑</option>
                  <option value="app:desc">{t('pages.ecosystemUsage.sortApp')} ↓</option>
                  <option value="app:asc">{t('pages.ecosystemUsage.sortApp')} ↑</option>
                  <option value="nombre:asc">{t('pages.ecosystemUsage.clinic')} ↑</option>
                  <option value="nombre:desc">{t('pages.ecosystemUsage.clinic')} ↓</option>
                </select>

                {!filtersOpen && activeFilterChips.length > 0 && (
                  <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                    {activeFilterChips.map((chip) => (
                      <button
                        key={chip.key}
                        type="button"
                        onClick={chip.onRemove}
                        className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-teal-500/50 bg-teal-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-teal-700 transition-colors hover:bg-teal-100 dark:border-teal-400/50 dark:bg-teal-900/20 dark:text-teal-200 dark:hover:bg-teal-900/40"
                        title={chip.label}
                      >
                        <span className="truncate">{chip.label}</span>
                        <span className="text-sm leading-none" aria-hidden>×</span>
                      </button>
                    ))}
                  </div>
                )}

                <p className="ml-auto hidden text-xs tabular-nums text-slate-500 dark:text-slate-400 md:block">
                  {(activeTab === 'monthly' ? monthlyRows : historyRows).length}
                </p>
              </div>

              {filtersOpen && (
                <div className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                  <div className="flex flex-col gap-2 lg:flex-row lg:items-end">
                    <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                      {activeTab === 'history' ? (
                        <div className="flex flex-col gap-0.5">
                          <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                            {t('pages.ecosystemUsage.monthFilter')}
                          </label>
                          <input
                            type="month"
                            value={historyPeriod}
                            min={historyMonthBounds.min}
                            max={historyMonthBounds.max}
                            onChange={(e) => {
                              if (e.target.value) setHistoryPeriod(e.target.value);
                            }}
                            className="block h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                            aria-label={t('pages.ecosystemUsage.monthFilter')}
                            title={t('pages.ecosystemUsage.monthFilter')}
                          />
                        </div>
                      ) : null}

                      <div className="flex flex-col gap-0.5 sm:col-span-2 xl:col-span-1">
                        <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          {t('pages.ecosystemUsage.filterName')}
                        </label>
                        <Input
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder={t('pages.ecosystemUsage.searchPlaceholder')}
                          className="h-8 py-1 text-xs"
                          aria-label={t('pages.ecosystemUsage.filterName')}
                          title={t('pages.ecosystemUsage.filterName')}
                        />
                      </div>

                      <div className="flex flex-col gap-0.5">
                        <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          {t('pages.ecosystemUsage.filterStatus')}
                        </label>
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                          className="block h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          aria-label={t('pages.ecosystemUsage.filterStatus')}
                        >
                          <option value="all">{t('pages.ecosystemUsage.statusAll')}</option>
                          <option value="active_or_trial">
                            {t('pages.ecosystemUsage.statusActiveOrTrial')}
                          </option>
                          <option value="active">{t('pages.ecosystemUsage.statusActive')}</option>
                          <option value="trial">{t('pages.ecosystemUsage.statusTrial')}</option>
                          <option value="suspended">
                            {t('pages.ecosystemUsage.statusSuspended')}
                          </option>
                        </select>
                      </div>

                      <div className="flex flex-col gap-0.5">
                        <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          {t('pages.ecosystemUsage.filterWhatsappMin')}
                        </label>
                        <input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          step={1}
                          value={whatsappMinRaw}
                          onChange={(e) => setWhatsappMinRaw(e.target.value)}
                          placeholder={t('pages.ecosystemUsage.whatsappMinPlaceholder')}
                          className="block h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-xs tabular-nums text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          aria-label={t('pages.ecosystemUsage.filterWhatsappMin')}
                        />
                      </div>

                      <div className="flex flex-col gap-0.5">
                        <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          {t('pages.ecosystemUsage.filterWhatsappMax')}
                        </label>
                        <input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          step={1}
                          value={whatsappMaxRaw}
                          onChange={(e) => setWhatsappMaxRaw(e.target.value)}
                          placeholder={t('pages.ecosystemUsage.whatsappMaxPlaceholder')}
                          className="block h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-xs tabular-nums text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          aria-label={t('pages.ecosystemUsage.filterWhatsappMax')}
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={clearFilters}
                      disabled={activeFilterChips.length === 0}
                      className="inline-flex h-8 w-full items-center justify-center rounded-md border border-slate-300 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-300 dark:hover:bg-gray-600 dark:hover:text-white lg:w-8 lg:shrink-0"
                      title={t('common.clear')}
                      aria-label={t('common.clear')}
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-8 0h10" />
                      </svg>
                      <span className="ml-2 text-sm lg:hidden">{t('common.clear')}</span>
                    </button>
                  </div>

                  {activeFilterChips.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {activeFilterChips.map((chip) => (
                        <button
                          key={chip.key}
                          type="button"
                          onClick={chip.onRemove}
                          className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-teal-500/50 bg-teal-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-teal-700 transition-colors hover:bg-teal-100 dark:border-teal-400/50 dark:bg-teal-900/20 dark:text-teal-200 dark:hover:bg-teal-900/40"
                          title={chip.label}
                        >
                          <span className="truncate">{chip.label}</span>
                          <span className="text-sm leading-none" aria-hidden>×</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <ClinicsUsageTable
              rows={activeTab === 'monthly' ? monthlyRows : historyRows}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              onViewFeatures={setClinicFeatures}
              onBonusWhatsapp={
                activeTab === 'monthly' ? setBonusTarget : undefined
              }
              onExtractInvoice={setInvoiceTarget}
              {...tableLabels}
            />
          </div>
        </>
      )}

      {showAdoptionModal ? (
        <FeaturesModal
          title={t('pages.ecosystemUsage.featureAdoption')}
          subtitle={t('pages.ecosystemUsage.featureAdoptionCount', {
            count: report?.featureAdoption.length ?? 0,
          })}
          emptyLabel={t('pages.ecosystemUsage.noFeatures')}
          closeLabel={t('common.close')}
          onClose={() => setShowAdoptionModal(false)}
          items={(report?.featureAdoption ?? []).map((item) => ({
            label: featureKeyLabel(item.featureKey),
            title: item.featureKey,
            value: `${item.tenantsWithFeature} ${t('pages.ecosystemUsage.clinicsShort', {
              count: item.tenantsWithFeature,
            })}`,
          }))}
        />
      ) : null}

      {clinicFeatures ? (
        <FeaturesModal
          title={clinicFeatures.nombre}
          subtitle={t('pages.ecosystemUsage.features')}
          emptyLabel={t('pages.ecosystemUsage.noFeatures')}
          closeLabel={t('common.close')}
          onClose={() => setClinicFeatures(null)}
          items={clinicFeatures.featuresEnabled.map((key) => ({
            label: featureKeyLabel(key),
            title: key,
          }))}
        />
      ) : null}

      {bonusTarget ? (
        <WhatsAppBonusModal
          tenantId={bonusTarget.tenantId}
          clinicName={bonusTarget.nombre}
          period={period}
          currentLimit={bonusTarget.whatsappMonthlyLimit}
          currentUsed={bonusTarget.whatsappMessages}
          currentBonus={bonusTarget.whatsappBonus}
          onClose={() => setBonusTarget(null)}
          onChanged={() => {
            void refetch();
          }}
        />
      ) : null}

      {invoiceTarget ? (
        <ClinicInvoiceModal
          tenantId={invoiceTarget.tenantId}
          period={activeTab === 'monthly' ? previousPeriod() : historyPeriod}
          clinicName={invoiceTarget.nombre}
          onClose={() => setInvoiceTarget(null)}
        />
      ) : null}
    </div>
  );
};
